/**
 * Food Index Page
 *
 * Landing page for the food module.
 * Provides navigation to recipes and ingredients.
 */

import { Head, Link, usePage } from '@inertiajs/react';
import { Navbar } from '@/components';
import { RecipeCard } from '@/components/food/RecipeCard';
import type { SharedProps } from '@/types';
import type { RecipeSummary } from '@/types/food';

interface FoodIndexProps extends SharedProps {
  stats: {
    totalRecipes: number;
    totalIngredients: number;
    recipesThisMonth: number;
    recentPreps: number;
  };
  recentRecipes: RecipeSummary[];
}

export default function FoodIndex() {
  const { stats, recentRecipes } = usePage<FoodIndexProps>().props;

  const features = [
    {
      title: 'Recipes',
      description: 'Browse, create, and manage your family recipes',
      href: '/food/recipes',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
          />
        </svg>
      ),
      color: 'orange',
      badge: stats.totalRecipes > 0 ? `${stats.totalRecipes} recipes` : undefined,
    },
    {
      title: 'Ingredients',
      description: 'Manage your ingredient library and substitutions',
      href: '/food/ingredients',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      ),
      color: 'green',
      badge: stats.totalIngredients > 0 ? `${stats.totalIngredients} items` : undefined,
    },
    {
      title: 'Import Recipe',
      description: 'Paste text from any source and let AI extract the recipe',
      href: '/food/recipes/import',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
          />
        </svg>
      ),
      color: 'purple',
    },
  ];

  const colorClasses: Record<string, { bg: string; bgHover: string; text: string; border: string }> = {
    orange: {
      bg: 'bg-orange-100',
      bgHover: 'group-hover:bg-orange-200',
      text: 'text-orange-600',
      border: 'hover:border-orange-300',
    },
    green: {
      bg: 'bg-green-100',
      bgHover: 'group-hover:bg-green-200',
      text: 'text-green-600',
      border: 'hover:border-green-300',
    },
    purple: {
      bg: 'bg-purple-100',
      bgHover: 'group-hover:bg-purple-200',
      text: 'text-purple-600',
      border: 'hover:border-purple-300',
    },
  };

  return (
    <>
      <Head title="Food" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />

        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Food</h1>
            <p className="text-gray-600">Manage your family recipes and ingredients.</p>
          </div>

          {/* Quick Stats */}
          {(stats.recipesThisMonth > 0 || stats.recentPreps > 0) && (
            <div className="mb-8 grid grid-cols-2 gap-4">
              {stats.recipesThisMonth > 0 && (
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-2xl font-bold text-orange-600">{stats.recipesThisMonth}</div>
                  <div className="text-sm text-gray-500">new recipes this month</div>
                </div>
              )}
              {stats.recentPreps > 0 && (
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="text-2xl font-bold text-green-600">{stats.recentPreps}</div>
                  <div className="text-sm text-gray-500">meals made in last 30 days</div>
                </div>
              )}
            </div>
          )}

          {/* Feature Cards */}
          <div className="grid gap-4 md:grid-cols-2 mb-8">
            {features.map((feature) => {
              const colors = colorClasses[feature.color];
              return (
                <Link
                  key={feature.href}
                  href={feature.href}
                  className={`block p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md ${colors.border} transition-all group`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${colors.bg} ${colors.bgHover} rounded-lg transition-colors`}>
                      <div className={colors.text}>{feature.icon}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                          {feature.title}
                        </h2>
                        {feature.badge && (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} rounded-full`}
                          >
                            {feature.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{feature.description}</p>
                    </div>
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Recent Recipes */}
          {recentRecipes.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Recipes</h2>
                <Link
                  href="/food/recipes"
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  View all
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentRecipes.map((recipe) => (
                  <RecipeCard key={recipe.id} recipe={recipe} />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {stats.totalRecipes === 0 && (
            <div className="text-center py-12 px-4 bg-white rounded-xl border border-gray-200">
              <div className="mb-4">
                <svg
                  className="w-16 h-16 mx-auto text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Get started with recipes</h2>
              <p className="text-gray-500 mb-6">
                Add your first recipe to start organizing your family's favorite meals.
              </p>
              <Link
                href="/food/recipes/new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg font-medium text-sm hover:bg-orange-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Your First Recipe
              </Link>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
